<template>
   <div class="table-content">
    <div class="table-wrap">
  <div class="table-title-div">
        <p class="table-title">Distribution Plots</p>
        <div class="qaqc-scorecard-header-actions">
          <div class="csv-btn" @click="downloadTableCSV">
            <v-img class="pointer" :src="documentDownload" alt="csv download"></v-img>
          </div>
          <v-btn
            icon
            variant="text"
            size="small"
            class="qaqc-scorecard-collapse-btn"
            :aria-label="isCollapsed ? 'Expand' : 'Collapse'"
            @click="isCollapsed = !isCollapsed"
          >
            <v-icon>{{ isCollapsed ? 'mdi-chevron-down' : 'mdi-chevron-up' }}</v-icon>
          </v-btn>
        </div>
      </div>
      </div>
      </div>
  <div class="qaqc-overview-distribution qaqc-initial-screening-plots-layout" v-show="!isCollapsed">
    <div class="qaqc-initial-screening-plot-selector qaqc-overview-plot-selector">
      <h6 class="qaqc-plot-selector-title">Plots</h6>
      <button
        v-for="opt in plotOptions"
        :key="opt.id"
        type="button"
        :class="['qaqc-initial-screening-plot-item', { active: selectedPlotId === opt.id }]"
        @click="$emit('update:selectedPlotId', opt.id)"
      >
        {{ opt.label }}
      </button>
    </div>
    <div class="qaqc-initial-screening-plot-panel qaqc-overview-plot-panel">
      <div>
        <div class="qaqc-test-chart-name-top">
          <h6>{{ selectedPlotLabel }}</h6>
          <div class="qaqc-test-header-actions qaqc-chart-actions"></div>
        </div>
        <div class="qaqc-distribution-chart-inner">
        <div ref="distChartRef" class="qaqc-distribution-bar-chart"></div>
        <div class="qaqc-overview-chart-legend qaqc-distribution-legend">
          <span class="qaqc-legend-item">
            <span class="qaqc-legend-dot valid-range"></span>
            {{ validRangeLabel }}
          </span>
          <span class="qaqc-legend-item clickable">
            <span class="qaqc-legend-dot outside-range"></span>
            {{ outsideRangeLabel }}
          </span>
        </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "DistributionPlotsSection",
  props: {
    plotOptions: { type: Array, default: () => [] },
    selectedPlotId: { type: String, default: "" },
    distributionData: {
      type: Object,
      default: () => ({
        categories: [],
        validRange: { values: [], label: "", count: 0, percent: "" },
        outsideRange: { count: 0, label: "" },
      }),
    },
  },
  emits: ["update:selectedPlotId"],
  data() {
    return {
      documentDownload: require("@/assets/Images/csv.svg"),
      isCollapsed: false,
    };
  },
  computed: {
    selectedPlotLabel() {
      const opt = this.plotOptions.find((o) => o.id === this.selectedPlotId);
      return (opt && opt.label) || "Relative Volume";
    },
    validRangeLabel() {
      const d = this.distributionData && this.distributionData.validRange;
      if (!d) return "Valid Range - not available";
      const label = d.label || "Valid Range";
      const count = d.count !== undefined && d.count !== null && d.count !== "" ? d.count : "-";
      const pct = d.percent !== undefined && d.percent !== null && d.percent !== "" ? d.percent : "-";
      return `${label} (${count} samples${pct !== "-" ? `, ${pct}` : ""})`;
    },
    outsideRangeLabel() {
      const d = this.distributionData && this.distributionData.outsideRange;
      return (d && d.label) || "Outside Range - not available";
    },
  },
  watch: {
    distributionData: {
      deep: true,
      handler() {
        this.$nextTick(() => this.renderBars());
      },
    },
  },
  mounted() {
    this.renderBars();
  },
  methods: {
    downloadTableCSV() {
      const categories = this.distributionData?.categories || [];
      const validValues = (this.distributionData?.validRange?.values) || [];
      if (categories.length === 0) return;
      const fileName = "distribution_plots.csv";
      const csvHeader = ["Category", "Count"];
      const csvRows = categories.map((cat, i) => [String(cat ?? ""), String(validValues[i] ?? "")]);
      const csvContent = [csvHeader.join(","), ...csvRows.map((row) => row.join(","))].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    },
    renderBars() {
      const el = this.$refs.distChartRef;
      if (!el) return;
      const categories = this.distributionData.categories || [];
      const validValues = (this.distributionData.validRange && this.distributionData.validRange.values) || [];
      if (categories.length === 0) {
        el.innerHTML = "";
        return;
      }
      const maxVal = Math.max(...validValues, 1);
      let html = '<div class="qaqc-distribution-bars">';
      categories.forEach((cat, i) => {
        const val = validValues[i] || 0;
        const pct = maxVal > 0 ? (val / maxVal) * 100 : 0;
        const isOut = i === 0 || i === categories.length - 1;
        const barClass = isOut ? "qaqc-dist-bar outside-range" : "qaqc-dist-bar valid-range";
        html += `
          <div class="qaqc-dist-bar-wrap">
            <div class="${barClass}" style="height: ${pct}%; min-height: ${val > 0 ? 8 : 0}px;" title="${cat}: ${val}"></div>
            <span class="qaqc-dist-bar-label">${cat}</span>
          </div>
        `;
      });
      html += "</div>";
      el.innerHTML = html;
    },
  },
};
</script>
